import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Users,
  UserCheck,
  UserX,
  TrendingUp,
  Plus,
  ArrowRight,
  Clock,
  AlertCircle,
  Search,
  Bell,
  User,
  Settings,
  Phone,
  MessageCircle,
  Mail,
  Megaphone,
  Instagram,
  Facebook,
  Store,
  Globe,
  UserPlus,
  BarChart3,
  Calendar,
  Filter,
  FileSearch,
  CheckCircle,
  Loader2,
  Building2
} from "lucide-react";
import { cn } from "@/lib/utils";

// Componente de barra para canal de prospecção
function CanalBar({ canal, total, maxTotal, color }) {
  const barWidth = maxTotal > 0 ? (total / maxTotal) * 100 : 0;
  
  return (
    <div className="flex items-center gap-2 py-1">
      <div className="w-28 text-xs font-medium truncate" title={canal}>{canal}</div>
      <div className="flex-1">
        <div 
          className={`h-5 ${color} rounded-sm flex items-center justify-end pr-2 transition-all`}
          style={{ width: `${Math.max(barWidth, 8)}%`, minWidth: total > 0 ? "30px" : "0" }}
        >
          {total > 0 && <span className="text-xs text-white font-medium">{total}</span>}
        </div>
      </div>
    </div>
  );
}

// Ícones por canal
const canalIcons = {
  "WHATSAPP": MessageCircle,
  "LIGAÇÃO CELULAR": Phone,
  "LIGAÇÃO FIXO": Phone,
  "INSTAGRAM": Instagram,
  "FACEBOOK": Facebook,
  "E-MAIL MKT": Mail,
  "SMS MKT": Megaphone,
  "PABX MKT": Phone,
  "SITE": Globe,
  "INDICAÇÃO": UserPlus,
  "PRESENCIAL": Store,
  "LOJA": Store,
};

// Cores por canal
const canalColors = {
  "WHATSAPP": "bg-green-500",
  "LIGAÇÃO CELULAR": "bg-blue-500",
  "LIGAÇÃO FIXO": "bg-blue-600",
  "INSTAGRAM": "bg-pink-500",
  "FACEBOOK": "bg-indigo-500",
  "E-MAIL MKT": "bg-yellow-500",
  "SMS MKT": "bg-purple-500",
  "PABX MKT": "bg-cyan-500",
  "SITE": "bg-teal-500",
  "INDICAÇÃO": "bg-amber-500",
  "PRESENCIAL": "bg-emerald-500",
  "LOJA": "bg-emerald-600",
  "NÃO INFORMADO": "bg-gray-400",
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentClients, setRecentClients] = useState([]);
  const [periodoFiltro, setPeriodoFiltro] = useState("30d");
  const [leadStats, setLeadStats] = useState({
    total: 0,
    porCanal: {},
    porInstituicao: {},
    porEsteira: {},
    porServico: {},
    novosHoje: 0,
    novosEstaSemana: 0,
    novosMes: 0
  });

  useEffect(() => {
    fetchData();
  }, [periodoFiltro]);

  const fetchData = async () => {
    try {
      const [statsRes, clientsRes, solicitacoesStatsRes] = await Promise.all([
        axios.get(`${API}/clientes/stats`),
        axios.get(`${API}/clientes?per_page=100`),
        axios.get(`${API}/solicitacoes/stats?periodo=${periodoFiltro}`)
      ]);
      setStats(statsRes.data);
      
      // Filtrar para EXCLUIR leads da base INSS que ainda não migraram
      // Eles só aparecem nos indicadores quando migrarem ou forem para CAPTAÇÃO
      const todosClientes = (clientsRes.data.clientes || []).filter(c => 
        c.esteira_atual !== "BASE" && c._fonte !== "leads_base"
      );
      
      // Filtrar apenas clientes que solicitaram simulação (solicitacoes_count > 0)
      const clientesComSimulacao = todosClientes
        .filter(c => (c.solicitacoes_count || 0) > 0)
        .sort((a, b) => new Date(b.ultima_solicitacao || 0) - new Date(a.ultima_solicitacao || 0))
        .slice(0, 5);
      setRecentClients(clientesComSimulacao);
      
      // Usar estatísticas de solicitações do backend
      const solStats = solicitacoesStatsRes.data;
      
      // Filtrar por período para clientes novos
      const now = new Date();
      let startDate = new Date();
      if (periodoFiltro === "1d") {
        // Hoje: começa à meia-noite de hoje
        startDate.setHours(0, 0, 0, 0);
      }
      else if (periodoFiltro === "7d") startDate.setDate(now.getDate() - 7);
      else if (periodoFiltro === "30d") startDate.setDate(now.getDate() - 30);
      else if (periodoFiltro === "90d") startDate.setDate(now.getDate() - 90);
      else if (periodoFiltro === "365d") startDate.setDate(now.getDate() - 365);
      else startDate = new Date(0);
      
      const clientesFiltrados = todosClientes.filter(c => {
        const createdAt = new Date(c.created_at);
        return createdAt >= startDate;
      });
      
      // Combinar dados de clientes com solicitações
      // Priorizar dados de solicitações se existirem
      const porCanalSolicitacoes = solStats.por_canal || {};
      const porCanalClientes = {};
      clientesFiltrados.forEach(c => {
        const canal = c.canal_prospeccao || "NÃO INFORMADO";
        porCanalClientes[canal] = (porCanalClientes[canal] || 0) + 1;
      });
      
      // Usar dados de solicitações se houver, senão usa de clientes
      const porCanal = Object.keys(porCanalSolicitacoes).length > 0 ? porCanalSolicitacoes : porCanalClientes;
      
      // Por instituição (das solicitações)
      const porInstituicao = solStats.por_instituicao || {};
      
      // Agrupar clientes por esteira
      const porEsteira = {};
      clientesFiltrados.forEach(c => {
        const esteira = c.esteira_atual || "SEM ESTEIRA";
        porEsteira[esteira] = (porEsteira[esteira] || 0) + 1;
      });
      
      setLeadStats({
        total: solStats.total || clientesFiltrados.length,
        porCanal,
        porInstituicao,
        porEsteira,
        porServico: solStats.por_servico || {},
        novosHoje: solStats.hoje || 0,
        novosEstaSemana: solStats.esta_semana || 0,
        novosMes: solStats.este_mes || 0
      });
      
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    } finally {
      setLoading(false);
    }
  };

  // Estados para o modal de Solicitar Simulação
  const [showSimulacaoModal, setShowSimulacaoModal] = useState(false);
  const [tipoBusca, setTipoBusca] = useState("cpf");
  const [buscaCliente, setBuscaCliente] = useState("");
  const [clientesEncontrados, setClientesEncontrados] = useState([]);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [canalSolicitacao, setCanalSolicitacao] = useState("");
  const [servicoSolicitado, setServicoSolicitado] = useState("");
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  const [registrandoSolicitacao, setRegistrandoSolicitacao] = useState(false);
  const [instituicoes, setInstituicoes] = useState([]);
  const [canaisProspeccao, setCanaisProspeccao] = useState([]);

  // Opções de tipo de busca
  const tiposBusca = [
    { id: "cpf", label: "CPF", placeholder: "000.000.000-00" },
    { id: "email", label: "E-mail", placeholder: "email@exemplo.com" },
    { id: "nb", label: "NB (Benefício)", placeholder: "Número do benefício" },
    { id: "telefone", label: "Telefone", placeholder: "DD + Número (ex: 11999999999)" },
  ];

  // Carregar instituições e canais
  useEffect(() => {
    const carregarDados = async () => {
      try {
        const [instRes, canaisRes] = await Promise.all([
          axios.get(`${API}/lookup/instituicoes`),
          axios.get(`${API}/lookup/canais-prospeccao`)
        ]);
        setInstituicoes(instRes.data.instituicoes || []);
        setCanaisProspeccao(canaisRes.data.canais || []);
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
      }
    };
    carregarDados();
  }, []);

  // Buscar cliente para simulação (usado no modal)
  const buscarClienteSimulacao = async () => {
    if (!buscaCliente.trim()) return;
    
    setBuscandoCliente(true);
    try {
      const response = await axios.get(`${API}/clientes?search=${encodeURIComponent(buscaCliente.trim())}`);
      setClientesEncontrados(response.data.clientes || []);
      setClienteSelecionado(null);
    } catch (error) {
      console.error("Erro na busca:", error);
      setClientesEncontrados([]);
    } finally {
      setBuscandoCliente(false);
    }
  };

  // Buscar cliente e abrir modal diretamente (usado no campo do header)
  const handleBuscarParaSimulacao = async () => {
    if (!buscaCliente.trim()) {
      setShowSimulacaoModal(true);
      return;
    }
    
    setBuscandoCliente(true);
    try {
      // Buscar pelo tipo selecionado
      let searchParam = `search=${encodeURIComponent(buscaCliente.trim())}`;
      if (tipoBusca === "cpf") {
        searchParam = `cpf=${encodeURIComponent(buscaCliente.trim().replace(/\D/g, ""))}`;
      } else if (tipoBusca === "email") {
        searchParam = `email=${encodeURIComponent(buscaCliente.trim())}`;
      } else if (tipoBusca === "nb") {
        searchParam = `nb=${encodeURIComponent(buscaCliente.trim())}`;
      } else if (tipoBusca === "telefone") {
        searchParam = `telefone=${encodeURIComponent(buscaCliente.trim().replace(/\D/g, ""))}`;
      }
      
      const response = await axios.get(`${API}/clientes?${searchParam}`);
      const clientes = response.data.clientes || [];
      setClientesEncontrados(clientes);
      
      // Se encontrou exatamente 1 cliente, já seleciona automaticamente
      if (clientes.length === 1) {
        setClienteSelecionado(clientes[0]);
      } else {
        setClienteSelecionado(null);
      }
      
      // Abre o modal
      setShowSimulacaoModal(true);
    } catch (error) {
      console.error("Erro na busca:", error);
      setClientesEncontrados([]);
      setShowSimulacaoModal(true);
    } finally {
      setBuscandoCliente(false);
    }
  };

  // Registrar solicitação de simulação
  const registrarSolicitacao = async () => {
    if (!clienteSelecionado || !canalSolicitacao || !servicoSolicitado) {
      return;
    }
    
    setRegistrandoSolicitacao(true);
    try {
      // Registrar a solicitação no histórico - o endpoint já atualiza o cliente
      const response = await axios.post(`${API}/solicitacoes`, {
        cliente_id: clienteSelecionado.id,
        cliente_nome: clienteSelecionado.nome,
        cliente_cpf: clienteSelecionado.cpf,
        cliente_instituicao: clienteSelecionado.primeira_instituicao,
        canal: canalSolicitacao,
        servico_solicitado: servicoSolicitado,
        instituicao: clienteSelecionado.primeira_instituicao,
        data_solicitacao: new Date().toISOString(),
        status: "CAPTACAO"
      });
      
      // Verificar se o cliente já estava em uma esteira ativa
      if (response.data.success === false) {
        alert(`⚠️ ${response.data.error}\n\nO cliente já está na esteira "${response.data.esteira_atual}".`);
        return;
      }
      
      // Labels para serviço solicitado
      const servicoLabels = {
        "SIMULACAO_CONSIGNADO": "Simulação Consignado 💰",
        "ANDAMENTO_CONTRATO": "Andamento do Contrato 📑",
        "OUTROS_ASSUNTOS": "Outros Assuntos ✍🏽"
      };
      
      // Fechar modal e recarregar dados
      setShowSimulacaoModal(false);
      setClienteSelecionado(null);
      setBuscaCliente("");
      setCanalSolicitacao("");
      setServicoSolicitado("");
      setClientesEncontrados([]);
      fetchData();
      
      // Mostrar sucesso (usando alert por simplicidade)
      const instNome = typeof clienteSelecionado.primeira_instituicao === 'object' 
        ? (clienteSelecionado.primeira_instituicao?.nome || "N/A")
        : (clienteSelecionado.primeira_instituicao || "N/A");
      alert(`✅ Solicitação registrada!\n\nCliente: ${clienteSelecionado.nome}\nCanal: ${canalSolicitacao}\nServiço: ${servicoLabels[servicoSolicitado] || servicoSolicitado}\nInstituição: ${instNome}\nEsteira: CAPTAÇÃO`);
      
    } catch (error) {
      console.error("Erro ao registrar solicitação:", error);
      alert("Erro ao registrar solicitação. Tente novamente.");
    } finally {
      setRegistrandoSolicitacao(false);
    }
  };

  const statCards = stats ? [
    {
      title: "Total de Clientes",
      value: stats.total_clientes,
      icon: Users,
      color: "text-blue-600",
      bgColor: "bg-blue-100",
    },
    {
      title: "Clientes Ativos",
      value: stats.ativos,
      icon: UserCheck,
      color: "text-green-600",
      bgColor: "bg-green-100",
    },
    {
      title: "Clientes Inativos",
      value: stats.inativos,
      icon: UserX,
      color: "text-red-600",
      bgColor: "bg-red-100",
    },
    {
      title: "Taxa de Conversão",
      value: stats.total_clientes > 0 
        ? `${Math.round((stats.ativos / stats.total_clientes) * 100)}%`
        : "0%",
      icon: TrendingUp,
      color: "text-purple-600",
      bgColor: "bg-purple-100",
    },
  ] : [];

  const esteiraColors = {
    CAPTACAO: "bg-blue-500",
    QUALIFICACAO: "bg-yellow-500",
    EM_ATENDIMENTO: "bg-orange-500",
    ANALISE_DOCUMENTAL: "bg-purple-500",
    APROVADO: "bg-green-500",
    RECUSADO: "bg-red-500",
    FINALIZADO: "bg-gray-500",
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" data-testid="dashboard-title">
              Dashboard
            </h1>
            <p className="text-muted-foreground mt-1">
              Bem-vindo ao Sistema StiloCred
            </p>
          </div>
          {/* Filtro de Período Global */}
          <div className="flex items-center gap-1 ml-2 pl-2 border-l">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={periodoFiltro} onValueChange={setPeriodoFiltro}>
              <SelectTrigger className="w-[130px] h-9" data-testid="periodo-filtro-global">
                <SelectValue placeholder="Período" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1d">Hoje</SelectItem>
                <SelectItem value="7d">Últimos 7 dias</SelectItem>
                <SelectItem value="30d">Últimos 30 dias</SelectItem>
                <SelectItem value="90d">Últimos 90 dias</SelectItem>
                <SelectItem value="365d">Último ano</SelectItem>
                <SelectItem value="all">Todo período</SelectItem>
              </SelectContent>
              </Select>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/clientes/novo">
            <Button className="gap-2" data-testid="new-client-btn">
              <Plus className="h-4 w-4" />
              Novo Cliente
            </Button>
          </Link>
          {/* Notifications */}
          <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
            <Bell className="h-5 w-5" />
            <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-red-500" />
          </Button>
          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-8 w-8 rounded-full" data-testid="user-menu-btn">
                <Avatar className="h-8 w-8">
                  <AvatarImage src="" alt="Usuário" />
                  <AvatarFallback className="bg-primary text-white">AD</AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end" forceMount>
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">Admin</p>
                  <p className="text-xs leading-none text-muted-foreground">admin@stilocred.com.br</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem>
                <User className="mr-2 h-4 w-4" />
                <span>Meu Perfil</span>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Settings className="mr-2 h-4 w-4" />
                <span>Configurações</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-red-600">
                Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {loading ? (
          Array(4).fill(0).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-2">
                <div className="h-4 bg-slate-200 rounded w-1/2"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-slate-200 rounded w-1/3"></div>
              </CardContent>
            </Card>
          ))
        ) : (
          statCards.map((card, index) => (
            <Card key={index} data-testid={`stat-card-${index}`}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {card.title}
                </CardTitle>
                <div className={cn("p-2 rounded-lg", card.bgColor)}>
                  <card.icon className={cn("h-4 w-4", card.color)} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{card.value}</div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* SEÇÃO NOVA: Indicadores de Entrada de Leads */}
      <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-white">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <BarChart3 className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-lg">📊 Entrada de Leads por Canal</CardTitle>
                <CardDescription>Acompanhe de onde seus leads estão vindo</CardDescription>
              </div>
            </div>
            <Link to="/dashboard-leads">
              <Button variant="outline" size="sm" className="gap-1">
                Ver Detalhes
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {/* Cards de Resumo Rápido */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-white rounded-lg border shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="h-4 w-4 text-green-500" />
                <span className="text-xs text-muted-foreground">Hoje</span>
              </div>
              <p className="text-2xl font-bold text-green-600">{leadStats.novosHoje}</p>
              <p className="text-xs text-muted-foreground">novos leads</p>
            </div>
            <div className="p-4 bg-white rounded-lg border shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="h-4 w-4 text-blue-500" />
                <span className="text-xs text-muted-foreground">Esta Semana</span>
              </div>
              <p className="text-2xl font-bold text-blue-600">{leadStats.novosEstaSemana}</p>
              <p className="text-xs text-muted-foreground">novos leads</p>
            </div>
            <div className="p-4 bg-white rounded-lg border shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="h-4 w-4 text-purple-500" />
                <span className="text-xs text-muted-foreground">Este Mês</span>
              </div>
              <p className="text-2xl font-bold text-purple-600">{leadStats.novosMes}</p>
              <p className="text-xs text-muted-foreground">novos leads</p>
            </div>
            <div className="p-4 bg-white rounded-lg border shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-4 w-4 text-amber-500" />
                <span className="text-xs text-muted-foreground">No Período</span>
              </div>
              <p className="text-2xl font-bold text-amber-600">{leadStats.total}</p>
              <p className="text-xs text-muted-foreground">total de leads</p>
            </div>
          </div>

          {/* Opção A: Cards Resumidos por Canal */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs">OPÇÃO A</span>
              Cards Resumidos por Canal
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {Object.entries(leadStats.porCanal)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 6)
                .map(([canal, total]) => {
                  const IconComponent = canalIcons[canal] || MessageCircle;
                  const bgColor = canalColors[canal] || "bg-gray-500";
                  const percent = leadStats.total > 0 ? ((total / leadStats.total) * 100).toFixed(0) : 0;
                  return (
                    <div 
                      key={canal} 
                      className="p-3 bg-white rounded-lg border shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className={`w-8 h-8 ${bgColor} rounded-full flex items-center justify-center mb-2`}>
                        <IconComponent className="h-4 w-4 text-white" />
                      </div>
                      <p className="text-xs font-medium text-slate-600 truncate" title={canal}>{canal}</p>
                      <div className="flex items-baseline gap-1 mt-1">
                        <span className="text-xl font-bold">{total}</span>
                        <span className="text-xs text-muted-foreground">({percent}%)</span>
                      </div>
                    </div>
                  );
                })}
              {Object.keys(leadStats.porCanal).length === 0 && (
                <div className="col-span-full text-center py-4 text-muted-foreground text-sm">
                  Nenhuma solicitação no período
                </div>
              )}
            </div>
          </div>

          {/* Indicadores por Instituição */}
          {Object.keys(leadStats.porInstituicao).length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Building2 className="h-4 w-4 text-purple-600" />
                Solicitações por Instituição
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {Object.entries(leadStats.porInstituicao)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 6)
                  .map(([inst, total]) => {
                    const percent = leadStats.total > 0 ? ((total / leadStats.total) * 100).toFixed(0) : 0;
                    // Extrair nome curto da instituição
                    const nomeInst = inst.includes(" - ") ? inst.split(" - ").slice(1).join(" - ") : inst;
                    return (
                      <div 
                        key={inst}
                        className="p-3 bg-white rounded-lg border shadow-sm hover:shadow-md transition-shadow border-l-4 border-l-purple-500"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Building2 className="h-4 w-4 text-purple-500" />
                        </div>
                        <p className="text-xs font-medium text-slate-600 truncate" title={inst}>{nomeInst || "N/A"}</p>
                        <div className="flex items-baseline gap-1 mt-1">
                          <span className="text-xl font-bold text-purple-700">{total}</span>
                          <span className="text-xs text-muted-foreground">({percent}%)</span>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Indicadores por Serviço Solicitado */}
          {Object.keys(leadStats.porServico).length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <FileSearch className="h-4 w-4 text-amber-600" />
                Solicitações por Tipo de Serviço
              </h4>
              <div className="grid grid-cols-4 gap-3">
                {(() => {
                  const servicoConfig = {
                    "SIMULACAO_CONSIGNADO": { label: "Simulação Consignado", emoji: "💰", color: "bg-green-500", textColor: "text-green-700", borderColor: "border-green-500" },
                    "ANDAMENTO_CONTRATO": { label: "Andamento do Contrato", emoji: "📑", color: "bg-blue-500", textColor: "text-blue-700", borderColor: "border-blue-500" },
                    "OUTROS_ASSUNTOS": { label: "Outros Assuntos", emoji: "✍🏽", color: "bg-orange-500", textColor: "text-orange-700", borderColor: "border-orange-500" },
                    "NÃO INFORMADO": { label: "Não Informado", emoji: "❓", color: "bg-gray-400", textColor: "text-gray-700", borderColor: "border-gray-400" }
                  };
                  
                  return Object.entries(leadStats.porServico)
                    .sort((a, b) => b[1] - a[1])
                    .map(([servico, total]) => {
                      const config = servicoConfig[servico] || servicoConfig["NÃO INFORMADO"];
                      const percent = leadStats.total > 0 ? ((total / leadStats.total) * 100).toFixed(0) : 0;
                      return (
                        <div 
                          key={servico}
                          className={`p-3 bg-white rounded-lg border-2 shadow-sm hover:shadow-md transition-shadow ${config.borderColor}`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-lg">{config.emoji}</span>
                            <p className="text-xs font-medium text-slate-600 truncate">{config.label}</p>
                          </div>
                          <div className="flex items-baseline gap-1">
                            <span className={`text-xl font-bold ${config.textColor}`}>{total}</span>
                            <span className="text-xs text-muted-foreground">({percent}%)</span>
                          </div>
                        </div>
                      );
                    });
                })()}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* INDICADORES DE ESTEIRA E EFICIÊNCIA */}
      <Card className="border-2 border-green-200 bg-gradient-to-r from-green-50 to-white">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <CardTitle className="text-lg">📈 Funil de Conversão por Esteira</CardTitle>
                <CardDescription>Acompanhe a eficiência do seu processo de vendas</CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Cards de Esteira */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
            {[
              { id: "CAPTACAO", label: "Captação", color: "bg-blue-500", textColor: "text-blue-700", bgLight: "bg-blue-50", borderColor: "border-blue-300" },
              { id: "EM_SIMULACAO", label: "Em Simulação", color: "bg-yellow-500", textColor: "text-yellow-700", bgLight: "bg-yellow-50", borderColor: "border-yellow-300" },
              { id: "COM_VALORES", label: "Com Valores", color: "bg-green-500", textColor: "text-green-700", bgLight: "bg-green-50", borderColor: "border-green-300" },
              { id: "SEM_VALORES", label: "Sem Valores", color: "bg-gray-500", textColor: "text-gray-700", bgLight: "bg-gray-50", borderColor: "border-gray-300" },
              { id: "SEM_INTERESSE", label: "Sem Interesse", color: "bg-orange-500", textColor: "text-orange-700", bgLight: "bg-orange-50", borderColor: "border-orange-300" },
              { id: "MARGEM_NEGATIVA", label: "Margem Negativa", color: "bg-red-400", textColor: "text-red-700", bgLight: "bg-red-50", borderColor: "border-red-300" },
              { id: "APROVADO", label: "Aprovado", color: "bg-emerald-500", textColor: "text-emerald-700", bgLight: "bg-emerald-50", borderColor: "border-emerald-300" },
              { id: "REPROVADO", label: "Reprovado", color: "bg-red-600", textColor: "text-red-700", bgLight: "bg-red-50", borderColor: "border-red-400" },
            ].map((esteira) => {
              const quantidade = leadStats.porEsteira[esteira.id] || leadStats.porEsteira[esteira.label.toUpperCase()] || leadStats.porEsteira[esteira.label] || 0;
              const totalEsteiras = Object.values(leadStats.porEsteira).reduce((a, b) => a + b, 0);
              const percent = totalEsteiras > 0 ? ((quantidade / totalEsteiras) * 100).toFixed(0) : 0;
              
              return (
                <div 
                  key={esteira.id}
                  className={`p-3 rounded-lg border-2 ${esteira.borderColor} ${esteira.bgLight} hover:shadow-md transition-shadow`}
                >
                  <div className={`w-full h-1 ${esteira.color} rounded mb-2`}></div>
                  <p className="text-xs font-medium text-slate-600 truncate">{esteira.label}</p>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className={`text-2xl font-bold ${esteira.textColor}`}>{quantidade}</span>
                    <span className="text-xs text-muted-foreground">({percent}%)</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Indicadores de Eficiência */}
          <div className="bg-white rounded-lg border p-4">
            <h4 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-green-600" />
              Indicadores de Eficiência
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {(() => {
                const captacao = leadStats.porEsteira["CAPTACAO"] || leadStats.porEsteira["Captação"] || 0;
                const emSimulacao = leadStats.porEsteira["EM_SIMULACAO"] || leadStats.porEsteira["Em Simulação"] || 0;
                const comValores = leadStats.porEsteira["COM_VALORES"] || leadStats.porEsteira["Com Valores"] || 0;
                const aprovado = leadStats.porEsteira["APROVADO"] || leadStats.porEsteira["Aprovado"] || 0;
                const semInteresse = leadStats.porEsteira["SEM_INTERESSE"] || leadStats.porEsteira["Sem Interesse"] || 0;
                const reprovado = leadStats.porEsteira["REPROVADO"] || leadStats.porEsteira["Reprovado"] || 0;
                const margemNegativa = leadStats.porEsteira["MARGEM_NEGATIVA"] || leadStats.porEsteira["Margem Negativa"] || 0;
                
                const totalEntradas = leadStats.total || captacao + emSimulacao + comValores + aprovado;
                const taxaSimulacao = captacao > 0 ? ((emSimulacao / captacao) * 100).toFixed(0) : 0;
                const taxaValores = emSimulacao > 0 ? ((comValores / emSimulacao) * 100).toFixed(0) : 0;
                const taxaAprovacao = totalEntradas > 0 ? ((aprovado / totalEntradas) * 100).toFixed(0) : 0;
                const taxaPerda = totalEntradas > 0 ? (((semInteresse + reprovado + margemNegativa) / totalEntradas) * 100).toFixed(0) : 0;
                
                return (
                  <>
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-xs text-blue-600 font-medium">Taxa de Simulação</p>
                      <p className="text-xs text-muted-foreground mb-1">Captação → Simulação</p>
                      <p className="text-2xl font-bold text-blue-700">{taxaSimulacao}%</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-xs text-green-600 font-medium">Taxa de Valores</p>
                      <p className="text-xs text-muted-foreground mb-1">Simulação → Com Valores</p>
                      <p className="text-2xl font-bold text-green-700">{taxaValores}%</p>
                    </div>
                    <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                      <p className="text-xs text-emerald-600 font-medium">Taxa de Aprovação</p>
                      <p className="text-xs text-muted-foreground mb-1">Total → Aprovado</p>
                      <p className="text-2xl font-bold text-emerald-700">{taxaAprovacao}%</p>
                    </div>
                    <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                      <p className="text-xs text-red-600 font-medium">Taxa de Perda</p>
                      <p className="text-xs text-muted-foreground mb-1">Sem Interesse + Reprovado</p>
                      <p className="text-2xl font-bold text-red-700">{taxaPerda}%</p>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Últimas Solicitações de Simulação */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle className="text-base">Últimas Solicitações</CardTitle>
              <CardDescription className="text-xs">Clientes que solicitaram simulação</CardDescription>
            </div>
            <Link to="/esteira-kanban">
              <Button variant="ghost" size="sm" className="gap-1 h-8">
                Ver esteira
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="pt-0">
            {loading ? (
              <div className="space-y-2">
                {Array(5).fill(0).map((_, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="h-8 w-8 bg-slate-200 rounded-full animate-pulse"></div>
                    <div className="flex-1 space-y-1">
                      <div className="h-3 bg-slate-200 rounded w-3/4"></div>
                      <div className="h-2 bg-slate-200 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : recentClients.length > 0 ? (
              <div className="space-y-1">
                {recentClients.map((cliente) => (
                  <Link
                    key={cliente.id}
                    to={`/clientes/${cliente.id}`}
                    className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
                    data-testid={`recent-client-${cliente.id}`}
                  >
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-primary font-semibold text-sm">
                        {cliente.nome?.charAt(0)?.toUpperCase() || "?"}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{cliente.nome}</p>
                      <p className="text-xs text-muted-foreground">{cliente.cpf}</p>
                    </div>
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 text-[10px] px-1.5 py-0 flex-shrink-0">
                      <FileSearch className="h-3 w-3 mr-0.5" />
                      {cliente.solicitacoes_count}x
                    </Badge>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-6 text-muted-foreground">
                <FileSearch className="h-6 w-6 mb-2" />
                <p className="text-sm">Nenhuma solicitação registrada</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Ações Rápidas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <Link to="/clientes/novo" className="block">
              <Button variant="outline" className="w-full h-20 flex flex-col gap-2" data-testid="quick-new-client">
                <Plus className="h-5 w-5" />
                <span>Novo Cliente</span>
              </Button>
            </Link>
            <Link to="/clientes" className="block">
              <Button variant="outline" className="w-full h-20 flex flex-col gap-2" data-testid="quick-list-clients">
                <Users className="h-5 w-5" />
                <span>Listar Clientes</span>
              </Button>
            </Link>
            <Button variant="outline" className="w-full h-20 flex flex-col gap-2" disabled>
              <TrendingUp className="h-5 w-5" />
              <span>Relatórios</span>
            </Button>
            <Button variant="outline" className="w-full h-20 flex flex-col gap-2" disabled>
              <AlertCircle className="h-5 w-5" />
              <span>Pendências</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Modal de Solicitar Simulação */}
      <Dialog open={showSimulacaoModal} onOpenChange={setShowSimulacaoModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <FileSearch className="h-6 w-6 text-red-600" />
              Solicitar Simulação / Análise
            </DialogTitle>
            <DialogDescription>
              Localize o cliente na base e registre a solicitação
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-4">
            {/* Busca de Cliente */}
            {!clienteSelecionado ? (
              <div className="space-y-3">
                <Label className="text-sm font-medium">1. Localizar Cliente</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Digite CPF, Nome ou Telefone..."
                      className="pl-10"
                      value={buscaCliente}
                      onChange={(e) => setBuscaCliente(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && buscarClienteSimulacao()}
                      autoFocus
                    />
                  </div>
                  <Button 
                    onClick={buscarClienteSimulacao}
                    disabled={buscandoCliente}
                    className="gap-2"
                  >
                    {buscandoCliente ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Search className="h-4 w-4" />
                    )}
                    Buscar
                  </Button>
                </div>

                {/* Lista de Clientes Encontrados */}
                {clientesEncontrados.length > 0 && (
                  <div className="border rounded-lg max-h-56 overflow-y-auto">
                    {clientesEncontrados.map((cliente) => (
                      <div
                        key={cliente.id}
                        onClick={() => setClienteSelecionado(cliente)}
                        className="flex items-center gap-3 p-3 cursor-pointer transition-colors border-b last:border-b-0 hover:bg-green-50"
                      >
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-primary font-semibold">
                            {cliente.nome?.charAt(0)?.toUpperCase() || "?"}
                          </span>
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{cliente.nome}</p>
                          <p className="text-sm text-muted-foreground">
                            CPF: {cliente.cpf} | Tel: {cliente.telefone_1 || "N/A"}
                          </p>
                        </div>
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                          Selecionar
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}

                {clientesEncontrados.length === 0 && buscaCliente && !buscandoCliente && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Nenhum cliente encontrado. Tente outro termo de busca.
                  </p>
                )}
              </div>
            ) : (
              <>
                {/* Cliente Selecionado */}
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="font-semibold text-green-800">Cliente Selecionado</span>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => {
                        setClienteSelecionado(null);
                        setCanalSolicitacao("");
                      }}
                      className="text-xs text-gray-500 hover:text-red-600"
                    >
                      Trocar cliente
                    </Button>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-full bg-green-200 flex items-center justify-center">
                      <span className="text-green-800 font-bold text-lg">
                        {clienteSelecionado.nome?.charAt(0)?.toUpperCase() || "?"}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-lg">{clienteSelecionado.nome}</p>
                      <p className="text-sm text-gray-600">
                        CPF: {clienteSelecionado.cpf} | Instituição: {typeof clienteSelecionado.primeira_instituicao === 'object'
                          ? (clienteSelecionado.primeira_instituicao?.nome || "N/A")
                          : (clienteSelecionado.primeira_instituicao || "N/A")}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Canal de Solicitação - ÍCONES CLICÁVEIS */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">2. Por qual canal o cliente solicitou?</Label>
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
                    {[
                      { id: "WHATSAPP", icon: MessageCircle, label: "WhatsApp", color: "bg-green-500 hover:bg-green-600" },
                      { id: "LIGAÇÃO CELULAR", icon: Phone, label: "Cel", color: "bg-blue-500 hover:bg-blue-600" },
                      { id: "LIGAÇÃO FIXO", icon: Phone, label: "Fixo", color: "bg-blue-600 hover:bg-blue-700" },
                      { id: "INSTAGRAM", icon: Instagram, label: "Instagram", color: "bg-pink-500 hover:bg-pink-600" },
                      { id: "FACEBOOK", icon: Facebook, label: "Facebook", color: "bg-indigo-600 hover:bg-indigo-700" },
                      { id: "E-MAIL MKT", icon: Mail, label: "E-mail", color: "bg-yellow-500 hover:bg-yellow-600" },
                      { id: "SMS MKT", icon: Megaphone, label: "SMS", color: "bg-purple-500 hover:bg-purple-600" },
                      { id: "PABX MKT", icon: Phone, label: "PABX", color: "bg-cyan-500 hover:bg-cyan-600" },
                      { id: "SITE", icon: Globe, label: "Site", color: "bg-teal-500 hover:bg-teal-600" },
                      { id: "INDICAÇÃO", icon: UserPlus, label: "Indicação", color: "bg-amber-500 hover:bg-amber-600" },
                      { id: "PRESENCIAL", icon: Store, label: "Loja", color: "bg-emerald-500 hover:bg-emerald-600" },
                      { id: "OUTROS", icon: AlertCircle, label: "Outros", color: "bg-gray-500 hover:bg-gray-600" },
                    ].map((canal) => {
                      const IconComp = canal.icon;
                      const isSelected = canalSolicitacao === canal.id;
                      return (
                        <button
                          key={canal.id}
                          type="button"
                          onClick={() => setCanalSolicitacao(canal.id)}
                          className={cn(
                            "flex flex-col items-center justify-center p-3 rounded-lg transition-all border-2",
                            isSelected 
                              ? `${canal.color} text-white border-transparent shadow-lg scale-105` 
                              : "bg-white hover:bg-gray-50 border-gray-200 hover:border-gray-300"
                          )}
                        >
                          <IconComp className={cn("h-6 w-6 mb-1", isSelected ? "text-white" : "text-gray-600")} />
                          <span className={cn("text-xs font-medium", isSelected ? "text-white" : "text-gray-600")}>
                            {canal.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Serviço Solicitado */}
                {canalSolicitacao && (
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">3. Qual Serviço Solicitado?</Label>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { id: "SIMULACAO_CONSIGNADO", label: "Simulação Consignado", emoji: "💰", color: "bg-green-500 hover:bg-green-600" },
                        { id: "ANDAMENTO_CONTRATO", label: "Andamento do Contrato", emoji: "📑", color: "bg-blue-500 hover:bg-blue-600" },
                        { id: "OUTROS_ASSUNTOS", label: "Outros Assuntos", emoji: "✍🏽", color: "bg-orange-500 hover:bg-orange-600" },
                      ].map((servico) => {
                        const isSelected = servicoSolicitado === servico.id;
                        return (
                          <button
                            key={servico.id}
                            type="button"
                            onClick={() => setServicoSolicitado(servico.id)}
                            className={cn(
                              "flex flex-col items-center justify-center p-4 rounded-lg transition-all border-2",
                              isSelected 
                                ? `${servico.color} text-white border-transparent shadow-lg scale-105` 
                                : "bg-white hover:bg-gray-50 border-gray-200 hover:border-gray-300"
                            )}
                          >
                            <span className="text-2xl mb-2">{servico.emoji}</span>
                            <span className={cn("text-xs font-medium text-center", isSelected ? "text-white" : "text-gray-600")}>
                              {servico.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Informação sobre esteira */}
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-blue-600" />
                  <p className="text-sm text-blue-800">
                    O cliente será direcionado para a <strong>Esteira CAPTAÇÃO</strong>
                  </p>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowSimulacaoModal(false);
              setClienteSelecionado(null);
              setBuscaCliente("");
              setCanalSolicitacao("");
              setServicoSolicitado("");
              setClientesEncontrados([]);
            }}>
              Cancelar
            </Button>
            <Button 
              onClick={registrarSolicitacao}
              disabled={!clienteSelecionado || !canalSolicitacao || !servicoSolicitado || registrandoSolicitacao}
              className="gap-2 bg-red-600 hover:bg-red-700"
            >
              {registrandoSolicitacao ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Registrando...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  Registrar Solicitação
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
